import type React from "react";
import { ChevronRight } from "lucide-react";
import { Skeleton } from "@planta-v2/pds-ui/skeleton";
import { DateUtils } from "@frontend/shared/presentation/utils/date.utils";
import { EventIdPublic } from "@shared/schemas/branded-id.schemas";
import { i18n } from "@frontend/i18n";
import { useYourEventsTabController } from "../controllers/useYourEventsTabController";
import type { MyEventViewUnion } from "@shared/schemas/event-views.schemas";

function formatEventDateRange(startAt: Date, endAt: Date): string {
  const locale = i18n.getLocale();
  const start = startAt;
  const end = endAt;
  const endStr = DateUtils.formatDate(end, locale);
  if (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate()
  ) {
    return endStr;
  }
  const startFormat =
    start.getFullYear() === end.getFullYear()
      ? new Intl.DateTimeFormat(locale, {
          month: "short",
          day: "numeric",
        }).format(start)
      : DateUtils.formatDate(start, locale);
  return `${startFormat} – ${endStr}`;
}

interface EventCardProps {
  readonly event: MyEventViewUnion;
  readonly onClick: (eventIdPublic: string) => void;
}

function EventCard({ event, onClick }: EventCardProps): React.JSX.Element {
  return (
    <button
      key={EventIdPublic.unwrap(event.idPublic)}
      type="button"
      className="flex w-full items-center gap-3 py-3 text-left border-b border-foreground/10 last:border-b-0 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={() => {
        onClick(EventIdPublic.unwrap(event.idPublic));
      }}
    >
      <div className="flex-1 min-w-0">
        <p className="font-serif text-base font-normal text-foreground leading-tight truncate">
          {event.name.content}
        </p>
        <p className="text-pds-2xs text-muted-foreground leading-tight">
          {formatEventDateRange(event.startAt, event.endAt)}
        </p>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </button>
  );
}

export function YourEventsTab(): React.JSX.Element {
  const { state, actions } = useYourEventsTabController();
  const { isLoading, upcoming, past } = state;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  const handleEventClick = actions.onEventClick;

  return (
    <div className="flex flex-col gap-6 p-4" data-testid="your-events-tab">
      {/* Upcoming Events */}
      <div className="flex flex-col" data-testid="upcoming-events-section">
        {upcoming.length === 0 && (
          <p className="text-pds-2xs text-muted-foreground">
            {i18n.t("user.account.events.noUpcoming")}
          </p>
        )}
        {upcoming.map((event) => (
          <EventCard
            key={EventIdPublic.unwrap(event.idPublic)}
            event={event}
            onClick={handleEventClick}
          />
        ))}
      </div>

      {/* Past Events */}
      <div className="flex flex-col" data-testid="past-events-section">
        <h2 className="mb-1 text-pds-micro font-medium uppercase tracking-[0.08em] text-muted-foreground">
          {i18n.t("user.account.events.past")}
        </h2>
        {past.length === 0 ? (
          <p className="text-pds-2xs text-muted-foreground">
            {i18n.t("user.account.events.noPast")}
          </p>
        ) : (
          past.map((event) => (
            <EventCard
              key={EventIdPublic.unwrap(event.idPublic)}
              event={event}
              onClick={handleEventClick}
            />
          ))
        )}
      </div>
    </div>
  );
}
